import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router';
import { BrandName } from '../brand/BrandName';
import { services } from '../../config/services';
import { siteConfig } from '../../config/site';

export function Header() {
  const routerLocation = useLocation();
  const [openPath, setOpenPath] = useState<string | null>(null);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const servicesRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const open = openPath === routerLocation.pathname;

  const closeMenu = () => {
    setOpenPath(null);
    setMobileServicesOpen(false);
  };
  const toggleMenu = () => setOpenPath(open ? null : routerLocation.pathname);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!servicesOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!servicesRef.current?.contains(event.target as Node)) setServicesOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [servicesOpen]);

  useEffect(() => {
    if (!open) return;
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    menuRef.current?.querySelector<HTMLElement>('button,a')?.focus();
    return () => { document.body.style.overflow = oldOverflow; };
  }, [open]);

  useEffect(() => {
    if (!open && !servicesOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setServicesOpen(false);
        closeMenu();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, servicesOpen]);

  const primary = siteConfig.primaryNavigation;
  const home = primary[0];
  const rest = primary.slice(1);

  return (
    <header className={`site-header ${scrolled ? 'site-header--scrolled' : ''}`}>
      <a className="skip-link" href="#main-content">Skip to content</a>
      <div className="shell site-header__inner">
        <Link className="wordmark" to="/" aria-label="Malev Studio home" onClick={closeMenu}><BrandName /></Link>
        <nav className="desktop-nav" aria-label="Primary navigation">
          <Link className="link-underline" to={home.href}>{home.label}</Link>
          <div className="desktop-services" ref={servicesRef}>
            <button className="link-underline icon-nudge" type="button" aria-expanded={servicesOpen} aria-controls="desktop-services-menu" onClick={() => setServicesOpen((value) => !value)}>
              Services <ChevronDown aria-hidden="true" size={15} />
            </button>
            {servicesOpen ? <div className="desktop-services__menu" id="desktop-services-menu">
              {services.map((service) => <Link className="link-underline" key={service.slug} to={`/services/${service.slug}`} onClick={() => setServicesOpen(false)}>{service.name}</Link>)}
            </div> : null}
          </div>
          {rest.map((item) => <Link className="link-underline" key={item.label} to={item.href}>{item.label}</Link>)}
        </nav>
        <Link to="/contact" className="header-cta">Get a Quote</Link>
        <button className="menu-button" type="button" aria-label={open ? 'Close menu' : 'Open menu'} aria-expanded={open} aria-controls="mobile-menu" onClick={toggleMenu}>{open ? <X /> : <Menu />}</button>
      </div>

      {open ? <div className="mobile-menu-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) closeMenu(); }}>
        <div id="mobile-menu" className="mobile-menu" role="dialog" aria-modal="true" aria-label="Mobile navigation" ref={menuRef}>
          <div className="mobile-menu__top">
            <span className="wordmark" aria-label={siteConfig.name}><BrandName /></span>
            <button className="menu-button" type="button" aria-label="Close menu" onClick={closeMenu}><X /></button>
          </div>
          <nav aria-label="Mobile navigation">
            <Link to={home.href} onClick={closeMenu}>{home.label}</Link>
            <button className="mobile-services__toggle" type="button" aria-expanded={mobileServicesOpen} aria-controls="mobile-services-menu" onClick={() => setMobileServicesOpen((value) => !value)}>
              Services<ChevronDown aria-hidden="true" size={20} />
            </button>
            {mobileServicesOpen ? <div className="mobile-services__menu" id="mobile-services-menu">
              {services.map((service) => <Link key={service.slug} to={`/services/${service.slug}`} onClick={closeMenu}>{service.name}</Link>)}
            </div> : null}
            {rest.map((item) => <Link key={item.label} to={item.href} onClick={closeMenu}>{item.label}</Link>)}
          </nav>
          <Link className="button-link button-link--primary mobile-menu__cta" to="/contact" onClick={closeMenu}>Get a Quote</Link>
        </div>
      </div> : null}
    </header>
  );
}
